import {
  AutoFillAliasDTO,
} from 'dtos';
import {
  isEmptyString,
  notEmptyString,
} from '@populateui/utils';
import {
  PdfTemplateField,
} from 'views/EMR/Settings/PdfTemplateBuilder/types';

export const AUTO_FILL_FIELD_TOOLTIP = 'Auto-filled by the system. Patients and clinicians will not fill this field.';

const ALIAS_GROUP_ORDER: Record<string, number> = {
  organization: 0,
  patient: 1,
  patientPrimaryInsurance: 2,
  appointment: 3,
};

export type AliasOption = {
  label: string;
  value: string;
  groupDisplayName: string;
  groupOrder: number;
};

type AliasField = {
  aliasId?: string | null;
};

export function buildAliasOptions(aliasOptions?: AutoFillAliasDTO[] | null): AliasOption[] {
  return (aliasOptions ?? [])
    .flatMap((aliasOption) => {
      const alias = aliasOption.alias ?? '';

      if (isEmptyString(alias)) {
        return [];
      }

      const groupKey = aliasOption.group?.aliasGroup ?? '';
      const groupDisplayName = aliasOption.group?.displayName ?? 'Other';

      return [{
        label: aliasOption.name ?? alias,
        value: alias,
        groupDisplayName,
        groupOrder: ALIAS_GROUP_ORDER[groupKey] ?? Number.MAX_SAFE_INTEGER,
      }];
    })
    .sort((left, right) => {
      if (left.groupOrder !== right.groupOrder) {
        return left.groupOrder - right.groupOrder;
      }

      return left.label.localeCompare(right.label);
    });
}

export function isValidAutoFillAliasId(
  aliasId: string | null | undefined,
  aliasOptions?: AutoFillAliasDTO[] | null,
): boolean {
  return notEmptyString(aliasId)
    && (aliasOptions ?? []).some((aliasOption) => aliasOption.alias === aliasId);
}

export function hasAutoFillAlias(
  field: AliasField,
  aliasOptions?: AutoFillAliasDTO[] | null,
): boolean {
  return isValidAutoFillAliasId(field.aliasId, aliasOptions);
}

export function resolveAliasLabel(
  aliasId: string | null | undefined,
  aliasOptions?: AutoFillAliasDTO[] | null,
): string | null {
  const normalizedAliasId = notEmptyString(aliasId) ? aliasId : null;
  const matchedAlias = normalizedAliasId
    ? (aliasOptions ?? []).find((aliasOption) => aliasOption.alias === normalizedAliasId)
    : undefined;

  return normalizedAliasId
    ? (matchedAlias?.name ?? matchedAlias?.alias ?? normalizedAliasId)
    : null;
}

export function getAutoFillFieldTooltip(
  aliasId: string | null | undefined,
  aliasOptions?: AutoFillAliasDTO[] | null,
): string {
  const aliasLabel = resolveAliasLabel(aliasId, aliasOptions);

  return aliasLabel
    ? `${AUTO_FILL_FIELD_TOOLTIP} (${aliasLabel})`
    : AUTO_FILL_FIELD_TOOLTIP;
}

export function formHasAutoFillFields(
  fields: PdfTemplateField[],
  aliasOptions?: AutoFillAliasDTO[] | null,
): boolean {
  return fields.some((field) => hasAutoFillAlias(field, aliasOptions));
}
